import { createRef, useState } from 'react';

import { filter, isEmpty, sortBy } from 'lodash-es';
import { useLocation, useNavigate } from 'react-router';
import { useParams } from 'react-router-dom';

import { useApolloClient } from '@apollo/client';
import SearchIcon from '@mui/icons-material/Search';
import SearchRounded from '@mui/icons-material/SearchRounded';
import {
  Box,
  Collapse,
  List,
  ListItem,
  ListItemButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import InkModal from '@/components/shared/InkModal/InkModal';
import usePrimaryNavStore from '@/components/shared/PrimaryNavDrawer/PrimaryNavStore';
import { useClientUserUpdateMutation } from '@/generated';
import { useSession } from '@/providers/SessionProvider/SessionProvider';
import { useToast } from '@/utils/useToast';

const SwitchAccountModal = () => {
  const { currentClient, clients } = useSession();
  const toast = useToast();
  const { clientId } = useParams();

  const navigate = useNavigate();
  const { pathname } = useLocation();

  const {
    switchAccountModalIsOpen,
    setSwitchAccountModalIsOpen,
    setIsDrawerOpen,
  } = usePrimaryNavStore();

  const cache = useApolloClient();

  const [updateClient] = useClientUserUpdateMutation({
    onError: (error) => toast(error.message, 'error'),
    onCompleted: () => {
      cache.resetStore();
      if (currentClient?.hasHardRefreshClientSwitcher) {
        window.location.reload();
      }
    },
  });

  const [accountSearchQuery, setAccountSearchQuery] = useState('');

  const [highlightedClientIndex, setHighlightedClientIndex] = useState(0);

  const handleAccountSearchQueryChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (event.target.value === 'ß') return;
    setHighlightedClientIndex(0);
    setAccountSearchQuery(event.target.value);
  };

  const handleSwitchAccountModalClose = () => {
    setSwitchAccountModalIsOpen(false);
    setAccountSearchQuery('');
    setIsDrawerOpen(false);
  };

  const handleClientUpdate = (newClientId: string | undefined) => {
    if (!newClientId) return;

    const pathParts = pathname.split('/');
    pathParts[1] = newClientId;
    const newPath = pathParts.join('/');

    handleSwitchAccountModalClose();
    navigate(newPath, {
      state: {
        isSwitchingAccount: true,
      },
    });
    updateClient({ variables: { id: newClientId } });
  };

  const handleScrollClientToView = (newIndex: number) => {
    const highlightedClientId = filteredClients?.[newIndex]?.id;
    if (!highlightedClientId) return;
    if (highlightedClientIndex) {
      listRefMap?.[highlightedClientId]?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  const handleKeyDownOnClientSearch = (key: string) => {
    const highlightedClientId = filteredClients?.[highlightedClientIndex]?.id;
    if (key === 'Enter' && highlightedClientId !== clientId) {
      handleClientUpdate(highlightedClientId);
    } else if (
      key === 'ArrowDown' &&
      filteredClients &&
      highlightedClientIndex < filteredClients?.length - 1
    ) {
      setHighlightedClientIndex((prev) => {
        const newIndex = prev + 1;
        handleScrollClientToView(newIndex);
        return newIndex;
      });
    } else if (key === 'ArrowUp' && highlightedClientIndex > 0) {
      setHighlightedClientIndex((prev) => {
        const newIndex = prev - 1;
        handleScrollClientToView(newIndex);
        return newIndex;
      });
    }
  };

  const clientsSortedByName = sortBy(clients, ['displayName']);
  const filteredClients = clientsSortedByName?.filter(({ displayName }) =>
    displayName.toLowerCase().includes(accountSearchQuery.toLowerCase()),
  );
  const hasSearchQuery = accountSearchQuery.length > 0;
  const searchResultIsEmpty = isEmpty(filteredClients);

  const listRefMap = filteredClients?.reduce<{
    [key: string]: React.RefObject<HTMLLIElement>;
  }>((acc, value) => {
    acc[value.id] = createRef<HTMLLIElement>();
    return acc;
  }, {});

  const highlightText = (accountName: string) => {
    if (!accountSearchQuery.trim()) return accountName;

    const escapedQuery = accountSearchQuery.replace(
      /[.*+?^${}()|[\]\\]/g,
      '\\$&',
    ); // Escape special characters
    const parts = accountName.split(new RegExp(`(${escapedQuery})`, 'gi'));

    return parts.map((part, index) =>
      part.toLowerCase() === accountSearchQuery.toLowerCase() ? (
        <Typography
          key={index}
          variant="caption"
          sx={{
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          {part}
        </Typography>
      ) : (
        part
      ),
    );
  };
  const recentAccounts = filter(
    clients ?? [],
    ({ id }) => id !== clientId,
  ).slice(0, 5);

  return (
    <InkModal
      open={switchAccountModalIsOpen}
      onClose={handleSwitchAccountModalClose}
      title="Switch Account"
      styles={{ overflow: 'hidden' }}
      dialogContentStyles={{
        overflowY: 'hidden',
        maxHeight: 480,
      }}
    >
      <Stack sx={{ gap: 2, pt: 1 }} data-cy="switch-account-modal">
        <TextField
          data-cy="account-search"
          id="account-search"
          fullWidth
          placeholder="Search..."
          value={accountSearchQuery}
          onChange={handleAccountSearchQueryChange}
          onKeyDown={(e) => handleKeyDownOnClientSearch(e.key)}
          sx={{
            '&.MuiInputBase-adornedStart > .MuiInputBase-input': {
              pl: 2,
            },
          }}
          slotProps={{
            input: {
              startAdornment: (
                <SearchIcon sx={{ color: 'neutral600', fontSize: 20 }} />
              ),
            },
          }}
          autoFocus
        />

        <Stack
          sx={{
            backgroundColor: 'neutral50',
            borderRadius: 2,
            px: 2,
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            height: 48,
          }}
        >
          <Typography variant="body2" sx={{ letterSpacing: '1px' }}>
            {currentClient?.displayName}
          </Typography>
          <Typography
            variant="overline"
            sx={{
              color: 'text.secondary',
              letterSpacing: '1px',
            }}
          >
            Current
          </Typography>
        </Stack>

        <Box
          sx={{
            height: 350,
            overflow: 'auto',
          }}
        >
          <Stack
            sx={{
              gap: 2,
            }}
          >
            <Collapse in={searchResultIsEmpty} unmountOnExit>
              <Stack
                sx={{
                  bgColor: 'neutral50',
                  height: 237,
                  gap: 1,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <SearchRounded sx={{ color: 'neutral500', fontSize: 64 }} />
                <Typography
                  sx={{
                    fontWeight: 600,
                  }}
                >
                  {`No results for "${accountSearchQuery}"`}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  Try another search term.
                </Typography>
              </Stack>
            </Collapse>

            <Collapse in={!hasSearchQuery} unmountOnExit>
              <Stack>
                <Typography
                  variant="overline"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 400,
                    letterSpacing: 1,
                    px: 2,
                  }}
                >
                  RECENT
                </Typography>

                <List sx={{ flexDirection: 'column', p: 0 }}>
                  {recentAccounts.map(({ id, displayName }) => (
                    <ListItem
                      key={id}
                      sx={{
                        p: 0,
                        cursor: clientId === id ? 'not-allowed' : '',
                      }}
                      ref={listRefMap?.[id]}
                    >
                      <ListItemButton
                        sx={{
                          py: 1,
                          px: 2,
                          ':hover': {
                            backgroundColor: 'blue50',
                          },
                          borderRadius: '8px',
                          '&:hover': {
                            backgroundColor: 'blue50',
                          },
                        }}
                        onClick={() => handleClientUpdate(id)}
                        disabled={clientId === id}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            letterSpacing: 0.17,
                          }}
                        >
                          {displayName}
                        </Typography>
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Stack>
            </Collapse>

            <Collapse in={!searchResultIsEmpty} unmountOnExit>
              <Stack>
                <Box sx={{ px: 2 }}>
                  {hasSearchQuery ? (
                    <Typography
                      variant="overline"
                      sx={{
                        color: 'text.secondary',
                        letterSpacing: 1,
                      }}
                    >
                      SEARCH RESULTS
                    </Typography>
                  ) : (
                    <Typography
                      variant="overline"
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 400,
                        letterSpacing: 1,
                      }}
                    >
                      ALL
                    </Typography>
                  )}
                </Box>

                <List sx={{ p: 0, pb: 2 }}>
                  {filteredClients.map(({ id, displayName }, index) => (
                    <ListItem
                      key={id}
                      sx={{
                        p: 0,
                        cursor: clientId === id ? 'not-allowed' : '',
                      }}
                      ref={listRefMap?.[id]}
                    >
                      <ListItemButton
                        sx={{
                          p: 1,
                          px: 2,
                          backgroundColor:
                            hasSearchQuery && index === highlightedClientIndex
                              ? 'blue50'
                              : 'inherit',
                          ':hover': {
                            backgroundColor: 'blue50',
                          },
                          borderRadius: '8px',
                          '&:hover': {
                            backgroundColor: 'blue50',
                          },
                        }}
                        onClick={() => handleClientUpdate(id)}
                        disabled={clientId === id}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            letterSpacing: 0.17,
                          }}
                        >
                          {highlightText(displayName)}
                        </Typography>
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Stack>
            </Collapse>
          </Stack>
        </Box>
      </Stack>
    </InkModal>
  );
};

export default SwitchAccountModal;
